import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Card, CardBody, Container } from 'reactstrap';
import dayjs from 'dayjs';
import { Box, Button, Typography, Paper, Tooltip, Fab, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { HUB_MANAGER_ROLE_ID, RIDER_AVAILABILITY_STATUS_OFF_DUTY, RIDER_AVAILABILITY_STATUS_ON_DUTY, RIDER_AVAILABILITY_STATUS_ON_VACATION } from 'src/utils/pnd/PndConstants';
import GroupUpdateAvaiabilityStatusModal from './GroupUpdateAvaiabilityStatusModal';
import PageHeader from 'src/shared/components/layout/PageHeader';

import DomainIcon from '@mui/icons-material/Domain';
import EditIcon from '@mui/icons-material/Edit';

import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import RadioSelectTable from 'src/shared/components/table/RadioSelectTable';


const AvailabityDetails = ({ reactTableData, dataInfo, onEdit }) => {
  const rows = reactTableData?.tableRowsData || [];
  const columns = reactTableData?.tableHeaderData || [];

  const [selectedRider, setSelectedRider] = useState(null);
  const [filterStatus, setFilterStatus] = useState(''); // '' = all
  const [isAssignOrderPopup, setAssignOrderPopup] = useState(false);

  const showDialogBox = () => setAssignOrderPopup(true);
  // filtered rows based on availability status
  const filteredRows = filterStatus ? rows.filter(r => r.riderAvailabilityId === Number(filterStatus)) : rows;

  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      <Container fluid style={{ maxWidth: '1400px' }}>
        <PageHeader
          icon={<DomainIcon fontSize="large" />}
          title="Rider Availability"
          subtitle="View and manage rider availability status."
          gradient="linear-gradient(135deg, #0284c7 0%, #0369a1 100%)"
        />
        <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
          {/* Left: Master List */}
          <Box sx={{ flex: 1, minWidth: 300 }}>
            {/* Availability Filter */}
            <FormControl variant="outlined" size="small" sx={{ mb: 2, minWidth: 180 }}>
              <InputLabel id="availability-filter-label">Status</InputLabel>
              <Select
                labelId="availability-filter-label"
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value=""><em>All</em></MenuItem>
                <MenuItem value={RIDER_AVAILABILITY_STATUS_ON_DUTY}>On Duty</MenuItem>
                <MenuItem value={RIDER_AVAILABILITY_STATUS_OFF_DUTY}>Off Duty</MenuItem>
                <MenuItem value={RIDER_AVAILABILITY_STATUS_ON_VACATION}>On Vacation</MenuItem>
              </Select>
            </FormControl>
            <RadioSelectTable
              rows={filteredRows}
              columns={columns}
              rowKey="riderId"
              selectedKey={selectedRider?.riderId}
              onSelect={setSelectedRider}
              searchKeys={['riderName']}
              searchPlaceholder="Search by Rider Name"
              pageSizes={[5, 10, 20]}
            />
          </Box>
          {/* Right: Detail Panel */}
          <Box sx={{ flex: 1, minWidth: 300 }}>
            {selectedRider ? (
              <Card>
                <CardBody>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6" gutterBottom>
                      {selectedRider.riderName}
                    </Typography>
                    {/* Edit button – opens the same modal used in the list view */}
                    <Tooltip title="Edit Rider">
                      <Fab
                        size="small"
                        color="primary"
                        sx={{
                          background: 'linear-gradient(45deg, #6a5acd, #00bfff)',
                          color: '#fff',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                          '&:hover': { background: 'linear-gradient(45deg, #5a4abd, #00a0e0)' },
                        }}
                        onClick={() => {
                          if (typeof onEdit === 'function') {
                            onEdit(selectedRider);
                          }
                        }}
                      >
                        <EditIcon />
                      </Fab>
                    </Tooltip>
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                  <Button
                    variant="contained"
                    color="primary"
                    size="small"
                    startIcon={<EventAvailableIcon />}
                    onClick={showDialogBox}
                    sx={{ ml: 1 }}
                  >
                    Group Update
                  </Button>
                </Box>
                  </Box>
                  <Typography variant="body2">
                    Availability: <strong>{selectedRider.riderAvailabilityDesc || '—'}</strong>
                  </Typography>

                  {/* Show Start and End Dates for Off Duty / Vacation */}
                  {(selectedRider.riderAvailabilityId === RIDER_AVAILABILITY_STATUS_OFF_DUTY || 
                    selectedRider.riderAvailabilityId === RIDER_AVAILABILITY_STATUS_ON_VACATION) && 
                    selectedRider.startDate && selectedRider.endDate && (
                      <Box sx={{ 
                        mt: 2, 
                        p: 2, 
                        bgcolor: '#f8fafc', 
                        borderRadius: '8px', 
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1
                      }}>
                        <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Status Schedule
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ color: '#475569' }}>Start Date:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {dayjs(selectedRider.startDate).format('YYYY-MM-DD HH:mm')}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" sx={{ color: '#475569' }}>End Date:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>
                            {dayjs(selectedRider.endDate).format('YYYY-MM-DD HH:mm')}
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  
                </CardBody>
              </Card>
            ) : (
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="subtitle1">Select a rider to view details</Typography>
              </Paper>
            )}
          </Box>
        </Box>

        {/* Modal */}
        {isAssignOrderPopup && (
          <GroupUpdateAvaiabilityStatusModal
            open={isAssignOrderPopup}
            setOpen={setAssignOrderPopup}
            dataInfo={dataInfo}
          />
        )}
      </Container>
    </Box>
  );
};

AvailabityDetails.propTypes = {
  reactTableData: PropTypes.shape({
    tableHeaderData: PropTypes.arrayOf(PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
    })),
    tableRowsData: PropTypes.arrayOf(PropTypes.shape()),
  }).isRequired,
  dataInfo: PropTypes.any,
  isNoRecordFound: PropTypes.bool,
};

export default AvailabityDetails;
